/**
 * Cloudflare Images base url, supports transformations. Used only for images.
 * https://developers.cloudflare.com/images/transform-images/transform-via-url/
 * Example: https://image.bridebook.com/assets/cms/supplier-pro/bridebook-team.jpg/w=250
 */
export const IMAGES_BASE_URL = 'https://image.bridebook.com';
export const BB_USER_IMAGES_BASE_URL = 'https://bb-user-images.bridebook.com';
export const PROXY_IMAGES_URL = 'https://bridebook.com/cdn-cgi/image';

/**
 * R2 bucket of static assets. Migrated from bridebook-imgs S3. Used for non-images static assets.
 */
export const ASSETS_BASE_URL = 'https://media.bridebook.com/assets';
export const OPTIMIZED_IMAGE_QUALITY = 80;

/**
 * Core parameters for Cloudflare Images
 */
interface CloudflareImageOptions {
  src: string;
  w?: number;
  h?: number;
  dpr?: number;
  fit?: 'cover' | 'contain' | 'crop' | 'pad';
  gravity?: 'face' | 'auto' | 'center' | string;
  forceCleanSrc?: boolean;
  blur?: number;
  quality?: number;
}

/**
 * Simple parameters for direct usage
 */
interface SimpleImageParams {
  path: string;
  w?: number;
  h?: number;
  dpr?: number;
  fit?: 'cover' | 'contain' | 'crop' | 'pad';
  gravity?: 'face' | 'auto' | 'center';
  blur?: number;
  quality?: number;
}

/**
 * Transforms Bridebook image URL to Cloudflare Images format
 */
const transformImageUrl = (src: string): string => {
  // FIXME: Temporary workaround before paths in FS are updated, see LIVE-13127
  const cleanSrc = src.replace('/suppliers/', '/weddingsuppliers/').replace('/public', '');

  // Replace Bridebook domain with Cloudflare Images endpoint
  return cleanSrc.replace('https://images.bridebook.com', IMAGES_BASE_URL);
};

/** Strip the IMAGES_BASE_URL prefix from a URL, returning the photo path.
 *  No-op when the input is already a path. */
export const toPhotoPath = (url: string): string => url.replace(`${IMAGES_BASE_URL}/`, '');

/**
 * CORE FUNCTION: Builds Cloudflare Images URL with new format
 * This is the single source of truth for URL generation
 */
export const buildCloudflareImageUrl = (options: CloudflareImageOptions): string => {
  const {
    src,
    w,
    h,
    dpr = typeof window === 'undefined' || window.devicePixelRatio > 1 ? 2 : 1,
    fit = 'cover',
    gravity = 'face',
    forceCleanSrc = false,
    blur,
    quality,
  } = options;

  // Transform URL unless forced to use clean source
  const baseUrl = forceCleanSrc ? src : transformImageUrl(src);

  // Build only the parameters that are provided
  const urlParams: string[] = [`dpr=${dpr}`];

  if (fit) urlParams.push(`fit=${fit}`);
  if (gravity) urlParams.push(`g=${gravity}`);
  if (w) urlParams.push(`w=${Math.round(w)}`);
  if (h) urlParams.push(`h=${Math.round(h)}`);
  if (blur) urlParams.push(`blur=${blur}`);
  if (quality) urlParams.push(`quality=${quality}`);

  return `${baseUrl}/${urlParams.join(',')}`;
};

/**
 * For direct img usage: Simple interface that mirrors old imgix pattern
 * Usage: buildImageSrc('/path/to/image.jpg', { w: 460, dpr: 2 })
 */
export const buildImageSrc = (
  imagePath = '',
  options: Omit<SimpleImageParams, 'path'> = {},
): string => {
  const fullSrc = /^https?/i.test(imagePath) ? imagePath : `${IMAGES_BASE_URL}/${imagePath}`;

  return buildCloudflareImageUrl({
    src: fullSrc,
    ...options,
  });
};

/**
 * Builds a Cloudflare Image Resizing URL (ie for Instagram media proxy)
 *
 * Takes an URL and returns a proxied URL that will resize
 * the image on-the-fly through Cloudflare's image transformation service.
 * This allows us to serve optimized images without storing them.
 *
 * @param sourceUrl - The original URL (e.g., from Instagram Graph API response)
 * @param options - Image transformation options
 * @param options.w - Target width in pixels
 * @param options.h - Target height in pixels
 * @returns Proxied URL that serves the original image with specified transformations
 *
 * @example
 * // Returns: https://bridebook.com/cdn-cgi/image/w=650,h=650,fit=cover/https://scontent-lhr6-1.cdninstagram.com/...
 * buildTransformationsProxyImageUrl('https://scontent-lhr6-1.cdninstagram.com/...', { w: 650, h: 650 })
 */
export const buildTransformationsProxyImageUrl = (
  sourceUrl: string,
  options: { w: number; h: number },
) => {
  const params = `w=${options.w},h=${options.h},fit=cover`;
  return `${PROXY_IMAGES_URL}/${params}/${sourceUrl}`;
};
